/**
 * We want to be able to have a plugin say "give me all annotations that belong
 * to a lesson or any of its screens, and tell me when anything changes". Since
 * mercury doesn't give us these relationships, we need to do it ourselves.
 */
export default class AnnotationsByCoursewareCache {
  constructor(motivation) {
    this.motivation = motivation;
    this.initialized = false;
    this._annotations = {};
    this._annotationsByCoursewareId = {};
    this._subscriptions = {};
  }

  getAnnotationsByCoursewareId(coursewareId) {
    if (!this._annotationsByCoursewareId[coursewareId]) {
      return [];
    }

    return this._annotationsByCoursewareId[coursewareId].map((annotationId) => this._annotations[annotationId]);
  }

  updateAnnotation(annotation) {
    this._annotations[annotation.id] = {
      ...this._annotations[annotation.id],
      annotation,
    };
  }

  getAnnotation(id) {
    return this._annotations[id];
  }

  _createUnsubscribe(coursewareId, listener) {
    return () => {
      const listenerIndex = this._subscriptions[coursewareId].indexOf(listener);
      this._subscriptions[coursewareId].splice(listenerIndex, 1);
    };
  }

  _notifySubscribers(coursewareIds) {
    for (const coursewareId of coursewareIds) {
      if (this._subscriptions[coursewareId]) {
        const listeners = this._subscriptions[coursewareId];
        const annotations = this.getAnnotationsByCoursewareId(coursewareId);
        listeners.forEach((listener) => listener(annotations));
      }
    }
  }

  _notifySubscribersByAnnotationId(annotationId) {
    const annotation = this._annotations[annotationId];
    const coursewareIds = [
      ...annotation.target.ancestry?.map(({ elementId }) => elementId),
      annotation.target.id,
    ];
    this._notifySubscribers(coursewareIds);
  }

  subscribe(coursewareId, listener) {
    this._subscriptions[coursewareId] = this._subscriptions[coursewareId] || [];
    this._subscriptions[coursewareId].push(listener);
    // Immediately notify the listener if we have relevant annotations
    if (this._annotationsByCoursewareId[coursewareId]) {
      const annotations = this.getAnnotationsByCoursewareId(coursewareId);
      listener(annotations);
    }

    return this._createUnsubscribe(coursewareId, listener);
  }

  saveAnnotation(annotation) {
    this._addAnnotation(annotation);
    this.initialized = true;
    this._notifySubscribersByAnnotationId(annotation.id);
  }

  saveAnnotations(annotations) {
    annotations.forEach(this._addAnnotation);
    this.initialized = true;
    this._notifySubscribers(Object.keys(this._annotationsByCoursewareId));
  }

  _addAnnotation = (annotation) => {
    if (typeof annotation.body === 'string' || typeof annotation.target === 'string') {
      throw new Error(`Invalid annotation format for ${annotation.id}. Expecting body and target to be parsed`);
    }

    this._annotations[annotation.id] = annotation;

    // Save a link from courseware ids back to annotations, as this is how we'll be
    // handling subscriptions
    this._addAnnotationIdByTarget(annotation.target.id, annotation.id);
    if (annotation.target.ancestry) {
      annotation.target.ancestry.forEach(({ elementId }) => {
        this._addAnnotationIdByTarget(elementId, annotation.id);
      });
    }

    this.annotationsCount = Object.keys(this._annotations);
  }

  _addAnnotationIdByTarget(targetId, annotationId) {
    this._annotationsByCoursewareId[targetId] = this._annotationsByCoursewareId[targetId] || [];
    this._annotationsByCoursewareId[targetId].push(annotationId);
  }
}
